import { Plus, RotateCcw, Trash2 } from 'lucide-react'
import {
  WORK_ORDER_NAME_SOURCES,
  WORK_ORDER_NAME_SOURCE_LABELS,
  workOrderSignatures,
  type WorkOrderNameSource,
  type WorkOrderSignatures,
} from '@symphenia/shared-types'
import { cardTitle, formCardSm, inputBase, selectBase } from '@/lib/form-styles'
import { FeldStatus, FeldStatusZeichen, useAenderung, useEntwurf, useFeld, useStellenDaten, type Speicherstelle } from '@/lib/speichern'

/**
 * AP-35 Nachtrag 10 — **Ausdruck des Auftrags** in den Projekteinstellungen
 * (Reiter Phasen): Briefpapier oder Logo, die Abschlusszeile und die Freigabe
 * nach EN 1090. Nicht jeder Betrieb unterschreibt dieselben Zeilen
 * (Auftraggeber 19.9.2026) — die Zeilen je Kategorie stehen darunter in
 * „Auftrag je Kategorie“.
 *
 * Gespeichert wird ohne Knopf (AP-36), auf der Stelle `projectSettings`: das
 * Papier sofort, die Unterschriften als **ein Eintrag** beim Verlassen der Karte
 * — sie sind ein Wert (`ProjectSetting.workOrderSignatures`), der Server säubert
 * ihn. Esc verwirft den Entwurf.
 */
export function AuftragAusdruckKarte({ stelle, darfAendern }: { stelle: Speicherstelle<unknown>; darfAendern: boolean }) {
  const d = useStellenDaten(stelle)
  const papier = useFeld(stelle, 'workOrderPaper', { art: 'auswahl', label: 'Gedruckt wird', pflicht: true, gesperrt: !darfAendern })
  const signaturen = d.workOrderSignatures
  const vorgabe = signaturen === null || signaturen === undefined
  const eintrag = useEntwurf<WorkOrderSignatures>(stelle, {
    kennung: 'workOrderSignatures',
    gespeichert: workOrderSignatures(signaturen),
    felder: ['workOrderSignatures'],
    label: 'Unterschriften des Auftrags',
    gesperrt: !darfAendern,
    operation: (neu) => () => ({ workOrderSignatures: neu }),
  })
  const zuruecksetzen = useAenderung(stelle, 'workOrderSignatures:vorgabe')
  const w = eintrag.entwurf ?? workOrderSignatures(signaturen)
  const setW = eintrag.setze
  const setzeZeile = (i: number, teil: Partial<WorkOrderSignatures['en1090']['lines'][number]>) =>
    setW((x) => ({ ...x, en1090: { ...x.en1090, lines: x.en1090.lines.map((z, j) => (j === i ? { ...z, ...teil } : z)) } }))

  return (
    <div className={formCardSm}>
      <h3 className={cardTitle}>Ausdruck des Auftrags</h3>
      <div className="mt-3 space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="auftrag-papier" className="w-64 shrink-0 text-muted-foreground">
            Gedruckt wird
          </label>
          <select id="auftrag-papier" className={`${selectBase} max-w-xs`} {...papier.props}>
            <option value="LETTERHEAD">auf dem Briefpapier</option>
            <option value="LOGO">auf weißem Blatt mit Logo</option>
          </select>
          <FeldStatusZeichen status={papier} />
          <span className="text-xs text-muted-foreground">Briefpapier und Logo kommen aus den Mandanten-Einstellungen. Ist kein Briefpapier hinterlegt, wird das Logo gedruckt.</span>
        </div>

        <FeldStatus status={papier} zeichen={false} />

        <div className="space-y-3" {...eintrag.huelle}>
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="auftrag-abschluss" className="w-64 shrink-0 text-muted-foreground">
            Abschlusszeile
          </label>
          <input
            id="auftrag-abschluss"
            className={`${inputBase} max-w-xs`}
            disabled={!darfAendern}
            value={w.closingLine ?? ''}
            placeholder="leer = keine Zeile"
            onChange={(e) => setW((x) => ({ ...x, closingLine: e.target.value }))}
          />
          <span className="text-xs text-muted-foreground">mit Ausführendem, Datum und Unterschrift — unter den Zeilen der Kategorie</span>
        </div>

        <div className="border-t border-border pt-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={!darfAendern}
              checked={w.en1090.enabled}
              onChange={(e) => setW((x) => ({ ...x, en1090: { ...x.en1090, enabled: e.target.checked } }))}
            />
            <span>Freigabe nach EN 1090 drucken</span>
            <span className="text-xs text-muted-foreground">— nur, wenn im Technik-Blatt eine Ausführungsklasse eingetragen ist</span>
          </label>

          {w.en1090.enabled && (
            <div className="mt-2 space-y-2 pl-6">
              <div className="flex flex-wrap items-center gap-3">
                <label htmlFor="auftrag-en1090-titel" className="w-[14.5rem] shrink-0 text-muted-foreground">
                  Überschrift
                </label>
                <input
                  id="auftrag-en1090-titel"
                  className={`${inputBase} max-w-xs`}
                  disabled={!darfAendern}
                  value={w.en1090.title}
                  onChange={(e) => setW((x) => ({ ...x, en1090: { ...x.en1090, title: e.target.value } }))}
                />
              </div>
              <table className="w-full max-w-2xl text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-1.5 pr-2">Rolle</th>
                    <th className="py-1.5 pr-2">Name vorbelegen aus dem Technik-Blatt</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {w.en1090.lines.map((z, i) => (
                    <tr key={i} className="border-b border-border/60">
                      <td className="py-1.5 pr-2">
                        <input
                          className={inputBase}
                          aria-label="Rolle"
                          disabled={!darfAendern}
                          value={z.label}
                          placeholder="z. B. Schweißaufsicht (SAP)"
                          onChange={(e) => setzeZeile(i, { label: e.target.value })}
                        />
                      </td>
                      <td className="py-1.5 pr-2">
                        <select
                          className={selectBase}
                          aria-label="Name vorbelegen"
                          disabled={!darfAendern}
                          value={z.nameFrom ?? ''}
                          onChange={(e) => setzeZeile(i, { nameFrom: (e.target.value || null) as WorkOrderNameSource | null })}
                        >
                          <option value="">— leer, zum Ausfüllen —</option>
                          {WORK_ORDER_NAME_SOURCES.map((q) => (
                            <option key={q} value={q}>
                              {WORK_ORDER_NAME_SOURCE_LABELS[q]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-1.5 text-right">
                        {darfAendern && (
                          <button
                            type="button"
                            className="p-1 text-muted-foreground hover:text-destructive"
                            aria-label="Zeile entfernen"
                            onClick={() => setW((x) => ({ ...x, en1090: { ...x.en1090, lines: x.en1090.lines.filter((_, j) => j !== i) } }))}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {darfAendern && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted"
                  onClick={() => setW((x) => ({ ...x, en1090: { ...x.en1090, lines: [...x.en1090.lines, { label: '', nameFrom: null }] } }))}
                >
                  <Plus size={13} /> Zeile
                </button>
              )}
            </div>
          )}
        </div>

        <FeldStatus status={eintrag} />
        </div>

        {darfAendern && !vorgabe && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => {
              eintrag.verwerfen()
              void zuruecksetzen.sende({ felder: ['workOrderSignatures'], patch: { workOrderSignatures: null } })
            }}
          >
            <RotateCcw size={12} /> Unterschriften auf die Vorgabe zurücksetzen
          </button>
        )}
        <FeldStatus status={zuruecksetzen.status} />
      </div>
    </div>
  )
}
